import React from "react";
import { useTranslation } from "react-i18next";
import { Mail, ArrowUpRight } from "lucide-react";
import p2bLogo from "../../assets/p2b_logo.webp";
import appStoreBadge from "../../assets/appStoreButton.svg";
import playStoreBadge from "../../assets/playStoreButton.svg";
import indiaFlag from "../../assets/india-flag-icon.webp";
import { APPSTORE_URL, PLAYSTORE_URL } from "../../constants/Constants";

/* ─────────────────────────────────────────────────────────────
   Footer — premium light footer, in sync with the P2B
   theme: pure white surface, signature indigo gradient accents
   (#4328E7 → #4328E7 → #6d63ff), Montserrat type and soft glows.
───────────────────────────────────────────────────────────── */

const COMPANY_LINKS = [
  { label: "About us", href: "/aboutUs/" },
  { label: "Meet the Team", href: "/meetTheTeam/" },
  { label: "Careers", href: "/careers/" },
  { label: "Contact", href: "/contact/" },
  { label: "Privacy Policy", href: "/privacyPolicy/" },
  { label: "Terms And Conditions", href: "/termsAndConditions/" },
  { label: "Refund And Cancellation", href: "/refundAndCancellation/" },
];

function Eyebrow({ children }) {
  return (
    <h3 className="mb-6 flex items-center gap-2 font-montserrat text-[11px] font-black uppercase tracking-[0.2em] text-[#4328E7]">
      <span className="h-px w-6 bg-[#4328E7]/30" />
      {children}
    </h3>
  );
}

function FooterLink({ label, href }) {
  return (
    <li>
      <a
        href={href}
        className="group inline-flex items-center gap-2 font-montserrat text-[15px] font-medium text-[#0d082899] transition-colors duration-200 hover:text-[#4328E7]"
      >
        <span className="h-[2px] w-0 rounded-full bg-gradient-to-r from-[#4328E7] to-[#4328E7] transition-all duration-300 group-hover:w-4" />
        {label}
      </a>
    </li>
  );
}

const Footer = React.memo(() => {
  const { t } = useTranslation();

  return (
    <footer className="relative overflow-hidden bg-white font-montserrat text-[#0d0828]">
      {/* signature gradient hairline */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#4328E7] via-[#4328E7] to-[#6d63ff]" />

      {/* soft brand glows */}

      <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:px-12">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1.25fr]">
          {/* ── brand ── */}
          <div className="sm:col-span-2 lg:col-span-1">
            <a href="/" className="inline-flex items-center gap-3" aria-label="P2B home">
              <img
                src={p2bLogo}
                alt=""
                aria-hidden="true"
                className="h-11 w-11 rounded-[12px]"
              />
              <span className="text-[26px] font-black tracking-[-0.02em] text-[#0d0828]">P2B</span>
            </a>

            <p className="mt-6 max-w-sm text-[15px] font-medium leading-7 text-[#0d082899]">
              <span className="font-semibold text-[#4328E7]">First month EMI free*</span>{" "}
              on all types of loans, 40+ banks &amp; lowest rates
            </p>

            {/* app is live — each badge deep-links straight to its store */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={APPSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="transition duration-300 hover:-translate-y-0.5"
              >
                <img src={appStoreBadge} alt="Download on the App Store" className="h-[46px] w-auto" />
              </a>
              <a
                href={PLAYSTORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="transition duration-300 hover:-translate-y-0.5"
              >
                <img src={playStoreBadge} alt="Get it on Google Play" className="h-[46px] w-auto" />
              </a>
            </div>
          </div>

          {/* ── company ── */}
          <div>
            <Eyebrow>{t("Company")}</Eyebrow>
            <ul className="space-y-3.5">
              {COMPANY_LINKS.map((l) => (
                <FooterLink key={l.href} label={t(l.label)} href={l.href} />
              ))}
            </ul>
          </div>

          {/* ── support ── */}
          <div>
            <Eyebrow>{t("Support")}</Eyebrow>
            <p className="max-w-xs text-[15px] font-medium leading-7 text-[#0d082899]">
              {t("Get Answers and Assistance Right Where You Need It")}
            </p>

            <a
              href="mailto:admin@plan2benefit.com"
              className="group mt-6 inline-flex items-center gap-3.5 rounded-2xl border border-[#ece9fb] bg-white px-4 py-3.5 shadow-[0_12px_30px_rgba(0,0,0,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-[#d9d4fb] hover:shadow-[0_20px_44px_rgba(0,0,0,0.07)]"
            >
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-[#4328E7] to-[#4328E7] text-white shadow-md shadow-black/5">
                <Mail className="h-5 w-5" strokeWidth={2} />
              </span>
              <span className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#0d082866]">
                  {t("Email us")}
                </span>
                <span className="text-[15px] font-bold tracking-[-0.01em] text-[#0d0828]">
                  admin@plan2benefit.com
                </span>
              </span>
              <ArrowUpRight className="h-4 w-4 flex-none text-[#4328E7] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>

        {/* ── bottom bar ── */}
        <div className="mt-14 flex flex-col gap-5 border-t border-[#ece9fb] pt-7 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
            <p className="text-[13px] font-medium text-[#0d082899]">
              &copy; 2021–2026 P2B. {t("All rights reserved")}.
            </p>
            <span className="hidden h-1 w-1 rounded-full bg-[#0d082833] sm:block" />
            <p className="text-[13px] font-medium text-[#0d082866]">
              Pardna Technologies Private Limited
            </p>
          </div>

          <div className="flex items-center">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#ece9fb] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#0d082899]">
              <img src={indiaFlag} alt="India" className="h-3.5 w-3.5 rounded-full object-cover" />
              Made in India
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";
export default Footer;
